import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { equipmentApi, type ApiEventType } from '@/api/equipmentApi'
import { useMockApi } from '@/lib/useMock'
import { useTenant } from '@/providers/TenantProvider'
import { eventMeta, type EventTypeCatalog } from '@/types/maintenanceTask'

/** A tenant event-type — identified by id, matched on schedules by `code`. */
export type EventTypeRow = {
  id: string
  code: string
  label: string
  /** Hex color, e.g. "#2563eb". */
  color: string
  order: number
  isSystem: boolean
}

/**
 * Seed catalog for mock mode — mirrors the backend's standard event-type
 * defaults (code + label + hex color) so the planner renders offline.
 */
const SEED_EVENT_TYPES: EventTypeRow[] = [
  { id: 'seed-pm', code: 'preventive_maintenance', label: 'Preventive Maintenance (PM)', color: '#2563eb', order: 0, isSystem: true },
  { id: 'seed-cm', code: 'corrective_maintenance', label: 'Corrective Maintenance', color: '#d97706', order: 1, isSystem: true },
  // One-off work such as a spill (z8nrz7f9u6).
  { id: 'seed-ca', code: 'corrective_action', label: 'Corrective Action', color: '#ea580c', order: 1, isSystem: true },
  { id: 'seed-clean', code: 'cleaning', label: 'Cleaning/Sanitization', color: '#16a34a', order: 2, isSystem: true },
  // Realigned to the backend catalog's violet-500. It had drifted to #7c3aed,
  // which is exactly the `process` colour — in mock mode the two chips were
  // indistinguishable. (The other colours in this list have drifted from the
  // backend too; left alone here rather than widening this change.)
  { id: 'seed-cal', code: 'calibration', label: 'Calibration', color: '#8b5cf6', order: 3, isSystem: true },
  { id: 'seed-insp', code: 'inspection', label: 'Inspection', color: '#0d9488', order: 4, isSystem: true },
  { id: 'seed-verify', code: 'verification', label: 'Verification', color: '#e11d48', order: 5, isSystem: true },
  { id: 'seed-pq', code: 'performance_qualification', label: 'Performance Qualification (PQ)', color: '#c026d3', order: 6, isSystem: true },
  // Not maintenance — a process-step booking. Present so mock mode resolves the
  // same label/colour the seeded catalog gives it.
  { id: 'seed-process', code: 'process', label: 'Process', color: '#7c3aed', order: 7, isSystem: true },
]

export function eventTypesQueryKey(tenantId: string | null) {
  return ['equipment-event-types', tenantId] as const
}

/**
 * The tenant's event types by code, for naming them (z8nrz7fb9k): the same
 * cached query as `useEventTypes`, without its mutations. Hand it to
 * `eventMeta(code, catalog)`. Empty until it loads; `eventMeta` falls back to
 * the standard names meanwhile.
 */
export function useEventTypeCatalog(): EventTypeCatalog {
  const { selectedTenantId } = useTenant()
  const tenantId = useMockApi ? 'mock' : selectedTenantId
  const query = useQuery({
    queryKey: eventTypesQueryKey(tenantId),
    enabled: tenantId !== null,
    queryFn: async (): Promise<EventTypeRow[]> => {
      if (useMockApi) return SEED_EVENT_TYPES
      const rows = await equipmentApi.listEventTypes(tenantId as string)
      return rows.map(toRow)
    },
  })
  return useMemo(() => new Map((query.data ?? []).map((t) => [t.code, { label: t.label, color: t.color }])), [query.data])
}

export type EventTypeMutations = {
  addEventType: (payload: { label: string; color: string; order?: number }) => Promise<void>
  renameOrRecolor: (
    id: string,
    payload: { label?: string; color?: string; order?: number },
  ) => Promise<void>
  removeEventType: (id: string) => Promise<void>
}

function sortEventTypes(rows: EventTypeRow[]): EventTypeRow[] {
  return [...rows].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
}

function toRow(t: ApiEventType): EventTypeRow {
  return { id: t.id, code: t.code, label: t.label, color: t.color, order: t.order, isSystem: t.isSystem }
}

/**
 * Tenant-scoped event-type catalog plus create/edit/delete mutations. The catalog
 * is the source of truth for chip/legend/calendar colors and labels. Mutations are
 * optimistic and refetch on both success and failure so backend errors (e.g. a 409
 * on deleting a system default) surface to the caller and roll the cache back to
 * server truth. A rename can change labels shown against schedules, so the equipment
 * query is invalidated after mutations too.
 */
export function useEventTypes(): {
  eventTypes: EventTypeRow[]
  isLoading: boolean
  error: string | null
  /** Resolve a code to its `{ label, color }` from the cached catalog. */
  resolveMeta: (code: string) => { label: string; color: string }
} & EventTypeMutations {
  const queryClient = useQueryClient()
  const { selectedTenantId } = useTenant()
  const tenantId = useMockApi ? 'mock' : selectedTenantId

  const queryKey = useMemo(() => eventTypesQueryKey(tenantId), [tenantId])

  const query = useQuery({
    queryKey,
    enabled: tenantId !== null,
    queryFn: async (): Promise<EventTypeRow[]> => {
      if (useMockApi) return SEED_EVENT_TYPES
      const rows = await equipmentApi.listEventTypes(tenantId as string)
      return rows.map(toRow)
    },
  })

  const eventTypes = useMemo(() => sortEventTypes(query.data ?? []), [query.data])

  const refetch = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      queryClient.invalidateQueries({ queryKey: ['equipment', tenantId] }),
    ])
  }, [queryClient, queryKey, tenantId])

  const setEventTypes = useCallback(
    (update: (prev: EventTypeRow[]) => EventTypeRow[]) =>
      queryClient.setQueryData<EventTypeRow[]>(queryKey, (prev) => update(prev ?? [])),
    [queryClient, queryKey],
  )

  const resolveMeta = useCallback(
    (code: string): { label: string; color: string } => {
      const match = queryClient.getQueryData<EventTypeRow[]>(queryKey)?.find((t) => t.code === code)
      // Never the raw code (z8nrz7fb9k): the standard name, else the code as words.
      return match ? { label: match.label, color: match.color } : eventMeta(code)
    },
    [queryClient, queryKey],
  )

  const addEventType = useCallback<EventTypeMutations['addEventType']>(
    async (payload) => {
      const order = payload.order ?? eventTypes.length
      setEventTypes((prev) => [
        ...prev,
        { id: `temp-${payload.label}`, code: payload.label, label: payload.label, color: payload.color, order, isSystem: false },
      ])
      if (useMockApi || !tenantId) return
      try {
        await equipmentApi.createEventType(tenantId, payload)
      } catch (err) {
        await refetch()
        throw err
      }
      await refetch()
    },
    [eventTypes.length, refetch, setEventTypes, tenantId],
  )

  const renameOrRecolor = useCallback<EventTypeMutations['renameOrRecolor']>(
    async (id, payload) => {
      setEventTypes((prev) => prev.map((t) => (t.id === id ? { ...t, ...payload } : t)))
      if (useMockApi || !tenantId) return
      try {
        await equipmentApi.updateEventType(tenantId, id, payload)
      } catch (err) {
        await refetch()
        throw err
      }
      await refetch()
    },
    [refetch, setEventTypes, tenantId],
  )

  const removeEventType = useCallback<EventTypeMutations['removeEventType']>(
    async (id) => {
      setEventTypes((prev) => prev.filter((t) => t.id !== id))
      if (useMockApi || !tenantId) return
      try {
        await equipmentApi.deleteEventType(tenantId, id)
      } catch (err) {
        await refetch()
        throw err
      }
      await refetch()
    },
    [refetch, setEventTypes, tenantId],
  )

  return {
    eventTypes,
    isLoading: query.isLoading || tenantId === null,
    error: query.error instanceof Error ? query.error.message : null,
    resolveMeta,
    addEventType,
    renameOrRecolor,
    removeEventType,
  }
}
